import { Dot, Verlet, VerletTicker } from '@tools/math/Verlet';
import type { Context } from './Carousel.svelte';
import type { Modifier } from './modifiers/Modifier';

export class Controller {
	public readonly modifiers: Modifier[] = [];
	public readonly context: Context<any>;

	private readonly verlet: Verlet = new Verlet();
	private readonly ticker: VerletTicker;

	public readonly main: Dot;
	private takeoverTarget?: Modifier;

	private fluidDots: Dot[] = [];
	private lastTick: number = -1;

	public contrains: { maxX: number; minX: number; maxY: number; minY: number } = {
		maxX: Number.MAX_SAFE_INTEGER,
		minX: Number.MIN_SAFE_INTEGER,
		maxY: Number.MAX_SAFE_INTEGER,
		minY: Number.MIN_SAFE_INTEGER
	};

	constructor(context: Context<any>) {
		this.context = context;
		$effect(() => {
			this.start();
			return () => {
				this.stop();
			};
		});

		this.ticker = new VerletTicker(this.verlet);
		this.main = this.verlet.addDot(0, 0, 0, 0);
		this.main.friction = 0.7;
	}

	public setContraints(constrains: { maxX: number; minX: number; maxY: number; minY: number }) {
		this.contrains = { ...this.contrains, ...constrains };
		this.main.contrains = { ...this.contrains };
		this.verlet.dots.forEach((dot) => (dot.contrains = { ...this.contrains }));
	}

	/**
	 * Create a connection between the main dot and a new dot
	 * @param type - A fluid connection will have its position set to the main dot each tick. A static connection won't
	 * @returns the references to the new dot and stick
	 **/
	public createConnection(type: 'fluid' | 'static' = 'fluid') {
		const dot = this.verlet.addDot(this.main.pos.x, this.main.pos.y);
		dot.pinned = true;
		dot.contrains = this.contrains;
		const stick = this.verlet.addStick(dot, this.main, 0);

		dot.contrains = { ...this.contrains };

		if (type === 'fluid') this.fluidDots.push(dot);

		return { dot, stick };
	}

	public add<T extends Modifier>(modifier: T): T {
		this.modifiers.push(modifier);
		for (let i = 0; i < this.modifiers.length; i++) {
			this.modifiers[i].__build(this);
		}

		$effect(() => {
			modifier.effect();
		});

		return modifier;
	}

	public get<T extends Modifier>(type: new (...args: any) => T): T | undefined {
		return this.modifiers.find((modifier) => modifier instanceof type) as T | undefined;
	}

	public isTakeoverTarget(target: Modifier) {
		return this.takeoverTarget === target;
	}
	public takeover(target: Modifier) {
		this.takeoverTarget = target;
		this.modifiers.forEach((modifier) => {
			if (target !== modifier) {
				modifier.lifeCycle.stop();
			} else {
				modifier.lifeCycle.start();
			}
		});
	}
	public giveback() {
		this.takeoverTarget = undefined;
		this.modifiers.forEach((modifier) => {
			modifier.lifeCycle.start();
		});
	}

	public tick() {
		if (this.modifiers.length === 0) return;

		this.modifiers.forEach((modifier) => {
			if (modifier.lifeCycle.isStarted()) {
				try {
					modifier.tick();
				} catch (e) {
					console.error(e);
				}
			}
		});

		const deltaTime = performance.now() - this.lastTick;
		this.lastTick = performance.now();

		this.ticker.update(deltaTime / 1000);
		this.context.state.drag = this.main.pos.x;
		this.fluidDots.forEach((dot) => dot.pos.set(this.main.pos));
	}

	public start() {
		// console.log('start');
		// this.context.state.drag = this.main.pos.x;
		this.modifiers.forEach((modifier) => modifier.lifeCycle.start());
		this.lastTick = performance.now();
	}

	public stop() {
		this.modifiers.forEach((modifier) => modifier.lifeCycle.stop());
	}
}
